import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'

// Fill these in from your Firebase project console:
// https://console.firebase.google.com → Project Settings → General → Your apps → Web app
const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
}

// VAPID key from: Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY

let app = null
let messaging = null

function isConfigured() {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId)
}

export function initFirebaseMessaging() {
  if (!isConfigured()) return null
  if (app) return messaging
  try {
    app = initializeApp(firebaseConfig)
    messaging = getMessaging(app)
    return messaging
  } catch (e) {
    console.warn('[fcm] Init failed:', e.message)
    return null
  }
}

/**
 * Request notification permission (only if not yet decided) and return FCM token.
 * - If permission is already 'granted', silently refreshes the token.
 * - If permission is 'denied', returns null without prompting.
 * - If permission is 'default' (first time), shows the browser dialog.
 * Returns null if Firebase is not configured or token cannot be obtained.
 */
export async function requestFcmToken() {
  const m = initFirebaseMessaging()
  if (!m) return null
  try {
    // Only prompt if the user hasn't decided yet
    if (Notification.permission === 'denied') return null
    if (Notification.permission !== 'granted') {
      const result = await Notification.requestPermission()
      if (result !== 'granted') return null
    }
    // updateViaCache:'none' ensures browser always re-fetches SW and picks up new config
    const swReg = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { updateViaCache: 'none' }
    )
    // Tell the new SW to activate immediately without waiting
    await swReg.update()
    const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg })
    return token || null
  } catch (e) {
    console.warn('[fcm] Token error:', e.message)
    return null
  }
}

/**
 * Listen for foreground messages. Returns unsubscribe function.
 */
export function onForegroundMessage(handler) {
  const m = initFirebaseMessaging()
  if (!m) return () => {}
  return onMessage(m, handler)
}
